/* Copyright Levelise Ltd 2019 - 2026 */
import config from '../config';
import TokenService from './token-service';

const FleetService = {
	async getUserPreferences(username) {
		const res = await fetch(`${config.REST_URI}/fleet/user/${username}/preferences`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async addUser(obj) {
		const res = await fetch(`${config.REST_URI}/fleet/user`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(obj),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async setUserPreferences(username, preferences) {
		const res = await fetch(`${config.REST_URI}/fleet/user/${username}/preferences`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(preferences),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFaultCode() {
		const res = await fetch(`${config.REST_URI}/fleet/faultcodes`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getRegionalDrus() {
		const res = await fetch(`${config.REST_URI}/fleet/regions/drus`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getDruList(username) {
		const res = await fetch(`${config.REST_URI}/fleet/user/${username}/drus`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getBatterySystems() {
		const res = await fetch(`${config.REST_URI}/fleet/batteries`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getBatterySystemDrus() {
		const res = await fetch(`${config.REST_URI}/fleet/batteries/drus`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getHwTanks() {
		const res = await fetch(`${config.REST_URI}/fleet/hw-tanks`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getDruStatuses(username) {
		const res = await fetch(`${config.REST_URI}/fleet/user/${username}/drus/status?optimised&identified`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getDruStatusesAsCsv(username) {
		return await fetch(`${config.REST_URI}/fleet/user/${username}/drus/status/csv`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
	},

	async getUserCustomersCSV(username, password) {
		const body = { password };

		return await fetch(`${config.REST_URI}/fleet/user/${username}/customers/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},

	async getCapacityMarketReportCsv(body) {
		return await fetch(`${config.REST_URI}/fleet/service/cm/report/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},

	async getCustomersFacilityStatusCSV(password) {
		const body = { password };

		return await fetch(`${config.REST_URI}/fleet/customers/facility/status/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},

	async getCustomersTariffStatusCSV(password) {
		const body = { password };

		return await fetch(`${config.REST_URI}/fleet/customers/tariff/status/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},

	async getCustomersConsentStatusCSV(password) {
		const body = { password };

		return fetch(`${config.REST_URI}/fleet/customers/consent/status/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},
	async getCustomersMeteringStatusCsv(password) {
		const body = { password };

		return fetch(`${config.REST_URI}/fleet/customers/metering/status/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},

	async getPaymentOutcome(password) {
		const body = { password };

		return fetch(`${config.REST_URI}/fleet/customers/credits/payment/outcome`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},
	async getExcludedPayments(password) {
		const body = { password };

		return fetch(`${config.REST_URI}/fleet/customers/credits/payment/excluded`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},

	async generatePaymentFiles() {
		const res = await fetch(`${config.REST_URI}/fleet/customers/credits/payment/request`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getDruStatusesForAggFacility(aggregated_facility) {
		const params = `/fleet/aggregatedfacility/${aggregated_facility}/drus/status?optimised&identified`;
		const res = await fetch(config.REST_URI + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFacilityByDruId(dru_id, currentOnly = false) {
		const params = !currentOnly ? `?since=0` : '';
		const res = await fetch(`${config.REST_URI}/fleet/dru/${dru_id}/facility${params}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomerByFacility(facility) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/customer`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getEnodeDevices(customerId, facilityName) {
		const res = await fetch(
			`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/enode/devices`,
			{
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
				},
			},
		);
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomerCredits(customerId, facilityName) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/credits`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomerFacilities(customerId) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/facilities`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomerConsents(customerId, facilityName) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/consent`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async updateConsent(customerId, facilityName, body) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/consent`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomerBankingInformation(customerId) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/settlement`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async disableCustomer(customerId) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/disable`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomerTariff(customerId, facilityName, tariffType) {
		const res = await fetch(
			`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/tariff/${tariffType}`,
			{
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
				},
			},
		);
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async updateCustomerTariff(customerId, facilityName, tariffType, body) {
		const res = await fetch(
			`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/tariff/${tariffType}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
				},
				body: JSON.stringify(body),
			},
		);
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async uploadCustomerFacilityFile(customerId, facilityName, typeObject) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/upload`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(typeObject),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async uploadBillToS3(url, method, body) {
		const res = await fetch(url, {
			method: method || 'PUT',
			body,
		});
		if (res.status === 200) {
			return Promise.resolve(res);
		}
		return await Promise.reject(res);
	},

	async getUserCustomers(username, ids = null, search = null) {
		const obj = {};
		if (ids) {
			obj.ids = ids;
		}
		if (search) {
			obj.search = search;
		}

		const params = new URLSearchParams(obj);

		const res = await fetch(`${config.REST_URI}/fleet/user/${username}/customers?${params.toString()}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomerFacilityUsers(customerId, facilityName) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/users`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async updateCustomer(customerId, changeObject) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(changeObject),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async modifyUser(username, changeObject) {
		const res = await fetch(`${config.REST_URI}/fleet/user/${username}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(changeObject),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFleetRecords(resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/records?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAfs() {
		const res = await fetch(`${config.REST_URI}/fleet/aggregatedfacilities?contracts`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAfsList() {
		const res = await fetch(`${config.REST_URI}/fleet/aggregatedfacilities`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAggFacilityRecords(aggregated_facility_name, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url =
			`${config.REST_URI}/fleet/aggregatedfacility/${aggregated_facility_name}/records?` +
			`resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAggFacilityPpm(aggregated_facility_name, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/aggregatedfacility/${aggregated_facility_name}/ppm?resolution=${
			resolution
		}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFleetConnectedDrus(resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/batteries/connected?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAggFacilityConnectedDrus(aggregated_facility_name, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url =
			config.REST_URI +
			`/fleet/aggregatedfacility/${aggregated_facility_name}/batteries/connected?` +
			`resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFleetSoc(resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/batteries/soc?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAggFacilitySoc(aggregated_facility_name, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url =
			config.REST_URI +
			`/fleet/aggregatedfacility/${aggregated_facility_name}/batteries/soc?` +
			`resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFleetTemp(resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/batteries/temperature?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAggFacilityTemp(aggregated_facility_name, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url =
			config.REST_URI +
			`/fleet/aggregatedfacility/${aggregated_facility_name}/batteries/temperature?` +
			`resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFleetFlow(resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/hw-tanks/flow?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAggFacilityFlow(aggregated_facility_name, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url =
			config.REST_URI +
			`/fleet/aggregatedfacility/${aggregated_facility_name}/hw-tanks/flow?` +
			`resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFleetHwTemperature(resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/hw-tanks/temperature?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getAggFacilityHwTemperature(aggregated_facility_name, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url =
			config.REST_URI +
			`/fleet/aggregatedfacility/${aggregated_facility_name}/hw-tanks/temperature?` +
			`resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFacilitiesWithinAggFacilities(aggregated_facility) {
		const res = await fetch(`${config.REST_URI}/fleet/aggregatedfacility/${aggregated_facility}/facilities`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async assignFacilityToAggFacility(facility, body) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/aggregatedfacility`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getUserFacilities(username) {
		const res = await fetch(`${config.REST_URI}/fleet/user/${encodeURIComponent(username)}/facilities`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFacility(facility, since = 0, withAddress = false) {
		const res = await fetch(
			`${
				config.REST_URI
			}/fleet/facility/${facility}?${since !== null ? 'since=' + since : ''}${withAddress ? '&address' : ''}`,
			{
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
				},
			},
		);
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async setFacility(facility, body) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async createCustomer(body) {
		const res = await fetch(`${config.REST_URI}/fleet/customer`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async setFacilityCommand(facility, command) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/dru-command?command=${command}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFacilityStatus(facility) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/system-status`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFacilityRecords(facility, resolution, since = null, before = null, count = null) {
		const param_obj = { since: since, before: before, count: count };
		const api_url = `${config.REST_URI}/fleet/facility/${facility}/records?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');
		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFacilityConnectivityHistory(facility, resolution, since = null, before = null) {
		const param_obj = { since: since, before: before };
		const api_url = `${config.REST_URI}/fleet/facility/${facility}/connectivity?resolution=${resolution}`;
		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');

		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getLogMessagesForFacility(facility, type, level, before, count = 50, serachTerm = '', signal = null) {
		const params = new URLSearchParams();
		if (type) {
			params.append('type', type);
		}
		if (level) {
			params.append('min-level', level);
		}
		if (before) {
			params.append('before', before);
		}
		if (count) {
			params.append('count', count);
		}
		if (serachTerm) {
			params.append('search', serachTerm);
		}

		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/messages?${params.toString()}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			signal,
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getFacilityLogsAsCSV(facility) {
		return await fetch(`${config.REST_URI}/fleet/facility/${facility}/messages/csv`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
	},

	async getFacilityQualifications(facility) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/qualifications`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async addNewFacility(facility) {
		const res = await fetch(`${config.REST_URI}/fleet/facility`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(facility),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getOperatingMode(facility) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/operating-mode`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async setOperatingMode(facility, mode) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facility}/operating-mode?mode=${mode}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async createCredits(body) {
		const res = await fetch(`${config.REST_URI}/fleet/customers/credits`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body,
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async uploadProcessedCredits(body) {
		const res = await fetch(`${config.REST_URI}/fleet/customers/credits/payment/complete`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body,
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getUserDetails(username) {
		const res = await fetch(`${config.REST_URI}/fleet/user/${username}/details`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async completeCustomerCredits(customerId, facilityName, body) {
		const res = await fetch(
			`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/credits/complete`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
				},
				body: JSON.stringify(body),
			},
		);

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async createCustomerCredits(customerId, facilityName, body, isFormerFacility = false) {
		const res = await fetch(
			`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/credits?${
				isFormerFacility ? 'former-facility' : ''
			}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
				},
				body: JSON.stringify(body),
			},
		);

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getCustomerFacilityFiles(customerId, facilityName) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/files`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getCustomerFile(customerId, fileId) {
		const res = await fetch(`${config.REST_URI}/fleet/customer/${customerId}/file/${fileId}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getInstallationCompanies() {
		const res = await fetch(`${config.REST_URI}/fleet/installation/companies`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getOrdersForInstallationCompany(companyId) {
		const res = await fetch(`${config.REST_URI}/fleet/installation/company/${companyId}/orders`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getCreditRuns() {
		const res = await fetch(`${config.REST_URI}/fleet/customers/credits/payment/runs`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getCreditRunFile(fileId) {
		const res = await fetch(`${config.REST_URI}/fleet/customers/credits/payment/file/${fileId}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getFacilityForecasts(facility, resolution, since = null, before = null, count = null) {
		const param_obj = { since: since, before: before, count: count };
		const api_url = `${config.REST_URI}/fleet/facility/${facility}/forecasts?resolution=${resolution}`;

		const params = Object.keys(param_obj).reduce((str, key) => {
			if (param_obj[key]) return str + '&' + key + '=' + param_obj[key];
			return str;
		}, '');

		const res = await fetch(api_url + params, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getCustomersTariffStatus() {
		const api_url = `${config.REST_URI}/fleet/customers/tariff/status`;

		const res = await fetch(api_url, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},

	async getCustomersConsentStatus() {
		const api_url = `${config.REST_URI}/fleet/customers/consent/status`;

		const res = await fetch(api_url, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getCustomersMeteringStatus() {
		const api_url = `${config.REST_URI}/fleet/customers/metering/status`;

		const res = await fetch(api_url, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getCustomerRecordsAsCSV(customerId, password, resolution, since = null, before = null) {
		const body = { password: password || '' };
		const params = new URLSearchParams({ resolution });

		if (since) {
			params.append('since', since);
		}
		if (before) {
			params.append('before', before);
		}

		return await fetch(`${config.REST_URI}/fleet/customer/${customerId}/records/csv?${params.toString()}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},
	async getDsrAllowedStatusDrus() {
		const res = await fetch(`${config.REST_URI}/fleet/drus/dsr-allowed/status`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async addCapacityMarketEvent(body) {
		const res = await fetch(`${config.REST_URI}/fleet/service/cm/event`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
		if (res.ok) {
			return res.json();
		}
		const e = await res.json();
		e.status = res.status;
		return await Promise.reject(e);
	},
	async getFacilityDirectives(facilityName, since = null, before = null, count = null) {
		const params = new URLSearchParams();

		if (since) {
			params.append('since', since);
		}
		if (before) {
			params.append('before', before);
		}
		if (count) {
			params.append('count', count);
		}

		const res = await fetch(`${config.REST_URI}/fleet/facility/${facilityName}/directives?${params.toString()}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;

		return await Promise.reject(e);
	},
	async getFacilityInstructions(facilityName, since = null, before = null, count = null) {
		const params = new URLSearchParams();

		if (since) {
			params.append('since', since);
		}
		if (before) {
			params.append('before', before);
		}
		if (count) {
			params.append('count', count);
		}

		const res = await fetch(`${config.REST_URI}/fleet/facility/${facilityName}/instructions?${params.toString()}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;

		return await Promise.reject(e);
	},
	async getFacilityData(facilityName, since = null, before = null, count = null) {
		const params = new URLSearchParams();

		if (since) {
			params.append('since', since);
		}
		if (before) {
			params.append('before', before);
		}
		if (count) {
			params.append('count', count);
		}

		const res = await fetch(`${config.REST_URI}/fleet/facility/${facilityName}/data?${params.toString()}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;

		return await Promise.reject(e);
	},
	async getCustomerFacilitySchedules(customerId, facilityName, since = null) {
		const params = new URLSearchParams();

		if (since) {
			params.append('since', since);
		}

		const res = await fetch(
			`${config.REST_URI}/fleet/customer/${customerId}/facility/${facilityName}/schedule?${params.toString()}`,
			{
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
				},
			},
		);

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;

		return await Promise.reject(e);
	},
	async getFacilitySchedules(facilityName) {
		const res = await fetch(`${config.REST_URI}/fleet/facility/${facilityName}/schedule`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;

		return await Promise.reject(e);
	},
	async getCustomersUploads(type = null, before = null, count = null) {
		const params = new URLSearchParams();

		if (before) {
			params.append('before', before);
		}

		if (count) {
			params.append('count', count);
		}

		if (type) {
			params.append('type', type);
		}

		const res = await fetch(`${config.REST_URI}/fleet/customers/uploads?${params.toString()}`, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
		});

		if (res.ok) {
			return res.json();
		}

		const e = await res.json();
		e.status = res.status;

		return await Promise.reject(e);
	},

	async generateInstallationReportCsv(body) {
		return await fetch(`${config.REST_URI}/fleet/installation/report/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},
	async generateQualificationTestsReportCsv(body) {
		return await fetch(`${config.REST_URI}/fleet/qualification/report/csv`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await TokenService.getDataFromAuthToken('access_token')}`,
			},
			body: JSON.stringify(body),
		});
	},
};

export default FleetService;
